import React, {FC, useEffect, useRef, useState} from "react"

import {Body, H1, H4, H6} from "@irati/ui/src/Typography"
import {MediaRental, WellnessMedia, WellnessMediaType} from "@irati/types"
import tracking from "@irati-tracking/wellness"
import mainTheme from "@irati/ui/src/global/theme"
import {FormattedMessage} from "@irati/utils/lang"
import {getMediaTypeCopy, upcomingEventDateAndTime, liveVideoDayText, startedStreaming} from "@irati/wellness/utils"
import Markdown from "@irati/ui/src/Markdown"
import Badge from "@irati/ui/src/Badge"
import Spacing from "@irati/ui/src/Spacing"
import moment from "moment-timezone"
import {hangPromise} from "@irati/utils/promise"
import Loading from "@irati/ui/src/Loading"
import wellnessService from "@irati/wellness/services/Wellness"
import {IconTypes} from "@irati/ui/src/Icon"
import {IconButton} from "@irati/ui/src/Button"
import Flex from "@irati/ui/src/Flex"

import MediaImportantTags from "../MediaImportantTags"
import {Card} from "./styled"

interface Props {
  media: WellnessMedia
  liveVideoInProgress: boolean
  isPremiumContent: boolean
  onShowAlert: () => void
}

const Index: FC<Props> = ({media, liveVideoInProgress, isPremiumContent, onShowAlert}) => {
  const {author, title, description, liveVideoStartTime, liveVideoEndTime, uuid, category} = media

  const pageLoadTime = useRef(Date.now())
  const scrollTrackingRef = useRef(false)
  const reachedBottomTime = useRef<number | null>(null)
  const trackingTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const [timestampString, setTimestampString] = useState<string>("")
  const [expiresAt, setExpiresAt] = useState<string>("")
  const [load, setLoad] = useState<Promise<any> | null>(hangPromise())
  const [shareAnimated, setShareAnimated] = useState(true)

  const fetchPremium = async () => {
    const loadData = await wellnessService.wellnessMediaRentals()
    const expiryDate = loadData?.results?.find((m: MediaRental) => m.mediaId === uuid)?.expiresAt

    setExpiresAt(expiryDate)
  }

  const handleOnIconClick = () => {
    setShareAnimated(false)
    onShowAlert()
  }

  const handleTrackingEvent = () => {
    if (scrollTrackingRef.current) return

    scrollTrackingRef.current = true
    tracking.viewMediaContentScrollToEnd(media)
  }

  const handleScroll = () => {
    if (scrollTrackingRef.current) return

    const windowHeight = window.innerHeight
    const documentHeight = document.documentElement.scrollHeight
    const scrollTop = window.scrollY || document.documentElement.scrollTop

    // Check if the user has scrolled to bottom
    if (scrollTop + windowHeight >= documentHeight - 100) {
      // Record when the user first reached the bottom if not already set
      if (reachedBottomTime.current === null) {
        reachedBottomTime.current = Date.now()
      }

      // Calculate time on page
      const timeOnPage = Date.now() - pageLoadTime.current

      // If already spent 15 seconds on page before reaching bottom => track immediately
      if (timeOnPage >= 15000) {
        handleTrackingEvent()
        window.removeEventListener("scroll", handleScroll)
      } else {
        // Set a timeout to track if the user stays until 15 seconds have passed
        const remainingTimeToTrack = 15000 - timeOnPage

        // Clear any existing timeout to avoid duplicates
        if (trackingTimeoutRef.current) {
          clearTimeout(trackingTimeoutRef.current)
        }

        // Set new timeout to fire event when total page time reaches 15 seconds
        trackingTimeoutRef.current = setTimeout(() => {
          handleTrackingEvent()
        }, remainingTimeToTrack)
      }
    } else {
      // If user scrolls away from bottom before tracking => cancel the timeout
      if (reachedBottomTime.current !== null && trackingTimeoutRef.current) {
        clearTimeout(trackingTimeoutRef.current)
        trackingTimeoutRef.current = null
        reachedBottomTime.current = null
      }
    }
  }

  useEffect(() => {
    setLoad(fetchPremium())
    const timer = setInterval(() => setTimestampString(startedStreaming(liveVideoStartTime)), 60000)
    setTimestampString(startedStreaming(liveVideoStartTime))

    if (!scrollTrackingRef.current) {
      window.addEventListener("scroll", handleScroll)
    }

    return () => {
      clearInterval(timer)
      window.removeEventListener("scroll", handleScroll)
      if (trackingTimeoutRef.current) {
        clearTimeout(trackingTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    tracking.viewMediaContent(media)
  }, [uuid])
  return (
    <Card marginTop={4}>
      <Flex width="100%" justifyContent="space-between" alignItems="center" marginBottom>
        <H1>{title}</H1>
        <IconButton
          icon={shareAnimated ? IconTypes.shareAnimated : IconTypes.share}
          onClick={handleOnIconClick}
          margin={[-1, -1, 0, 0]}
          size={24}
        />
      </Flex>
      {media.type !== WellnessMediaType.LiveVideo && isPremiumContent && (
        <Spacing marginBottom={2}>
          <Badge color={mainTheme.colors.primary1} uppercase weight="semibold">
            <FormattedMessage id="wellness.rentalMedia.premium" />
          </Badge>
        </Spacing>
      )}
      {media.type === WellnessMediaType.LiveVideo && !liveVideoInProgress && (
        <Body color={mainTheme.colors.utility.contrast} marginBottom={2}>
          <FormattedMessage id="platform.liveVideo.liveOn" />
          {liveVideoDayText(liveVideoStartTime)}, {upcomingEventDateAndTime(liveVideoStartTime, liveVideoEndTime)}
        </Body>
      )}
      {media.type === WellnessMediaType.LiveVideo && liveVideoInProgress && (
        <Body color={mainTheme.colors.utility.contrast} marginBottom={2}>
          <FormattedMessage id="platform.liveVideo.startedStreaming" /> {timestampString}
        </Body>
      )}
      {category && <MediaImportantTags media={media} color={mainTheme.colors.utility.contrast} />}

      {media.type !== WellnessMediaType.LiveVideo && isPremiumContent && !!media?.src?.length && (
        <Loading load={load}>
          <H6 marginTop={2}>
            <FormattedMessage id="wellness.rentalMedia.countdown" values={{remainingTime: moment(expiresAt).toNow(true)}} />
          </H6>
        </Loading>
      )}

      {media.type !== WellnessMediaType.Article && (
        <H4 marginTop={5} marginBottom>
          <FormattedMessage id="wellness.media.description" values={{mediaType: getMediaTypeCopy(media.type)}} />
        </H4>
      )}

      <Markdown source={description} />
      {author && (
        <>
          <H4 marginBottom marginTop={3}>
            <FormattedMessage id="wellness.media.author.description" />
          </H4>
          <Markdown source={author.description} />
        </>
      )}
    </Card>
  )
}

export default Index
